// apps/landing-page/src/components/nav/MobileNavAccordion.tsx
'use client';

import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

export interface MobileNavAccordionProps {
  id: string;
  label: string;
  isOpen: boolean;
  isActive: boolean;
  onToggle: () => void;
  children: ReactNode;
}

/**
 * Shared shell for the mobile menu's collapsible sections (Paket Ujian, Program).
 * Structurally simpler than NavDropdown — an inline expand under a full-width
 * button rather than a floating panel — so it doesn't need outside-click or
 * Escape handling; the whole mobile menu is already a dismissible overlay.
 */
export function MobileNavAccordion({ id, label, isOpen, isActive, onToggle, children }: MobileNavAccordionProps) {
  const panelId = `mobile-nav-accordion-panel-${id}`;

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={panelId}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg font-medium text-sm ${
          isActive ? 'bg-teal-50 text-teal-600' : 'text-slate-700 hover:bg-teal-50 hover:text-teal-600'
        }`}
      >
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div id={panelId} className="mt-1 px-1 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
}
